import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, BookOpen } from "lucide-react";
import { listPhrases, createPhrase, deletePhrase } from "@/lib/api";
import { toast } from "sonner";

export default function Dictionary({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(false);

  const [newText, setNewText] = useState("");
  const [newTags, setNewTags] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPhrases({ q: q || undefined, tag: tag || undefined, limit: 200 });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Failed to load dictionary");
    } finally {
      setLoading(false);
    }
  }, [q, tag]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const add = async () => {
    if (!newText.trim()) return;
    setAdding(true);
    try {
      const tagList = newTags.split(",").map((t) => t.trim()).filter(Boolean);
      await createPhrase({ text: newText, tags: tagList, notes: newNotes });
      toast.success(`Added "${newText}"`);
      setNewText("");
      setNewTags("");
      setNewNotes("");
      load();
      onChange?.();
    } catch {
      toast.error("Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const del = async (id, text) => {
    if (!window.confirm(`Delete "${text}"?`)) return;
    try {
      await deletePhrase(id);
      toast.success("Deleted");
      load();
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <section className="space-y-10">
      <div className="rise">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
          // Phrase Dictionary
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
          <span className="gold italic">{total}</span> phrases. Add yours.
        </h1>
        <p className="muted mt-4 max-w-2xl leading-relaxed">
          Browse the curated seed list, add your own phrases, or remove ones you don&apos;t want.
          Every phrase is automatically scored across all 20 ciphers on creation.
        </p>
      </div>

      {/* ADD FORM */}
      <div className="rise rise-1 panel p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="gold" size={18} />
          <h2 className="font-display text-2xl">Add Phrase</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-wider muted">Phrase</label>
            <input
              data-testid="new-phrase-text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              className="terminal-input mt-1.5 w-full px-4 py-3"
              placeholder='e.g. "Eclipse Of The Heart"'
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider muted">Tags (comma-separated)</label>
            <input
              data-testid="new-phrase-tags"
              value={newTags}
              onChange={(e) => setNewTags(e.target.value)}
              className="terminal-input mt-1.5 w-full px-4 py-3"
              placeholder="ritual, occult, death"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider muted">Notes (optional)</label>
            <input
              data-testid="new-phrase-notes"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              className="terminal-input mt-1.5 w-full px-4 py-3"
              placeholder="Context for this phrase…"
            />
          </div>
        </div>
        <button
          data-testid="add-phrase-btn"
          onClick={add}
          disabled={adding || !newText.trim()}
          className="btn-primary mt-5 px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
        >
          {adding ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
          Add to Dictionary
        </button>
      </div>

      {/* SEARCH / FILTER */}
      <div className="rise rise-2 flex flex-wrap gap-3">
        <input
          data-testid="dict-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search phrases…"
          className="terminal-input px-4 py-3 flex-1 min-w-[200px]"
        />
        <input
          data-testid="dict-tag-filter"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="Filter by tag (e.g. ritual)…"
          className="terminal-input px-4 py-3 w-[260px]"
        />
      </div>

      {/* LIST */}
      <div className="rise rise-3 panel">
        {renderList(loading, items, del)}
      </div>
    </section>
  );
}

function renderList(loading, items, del) {
  if (loading) {
    return (
      <div className="p-10 flex justify-center muted">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="p-10 text-center muted font-mono text-sm">
        No phrases match.
      </div>
    );
  }
  return (
    <ul data-testid="phrase-list">
      {items.map((p) => (
        <li
          key={p.id}
          className="border-b border-white/10 last:border-0 px-5 py-4 flex items-center gap-4 hover:bg-white/[0.02]"
          data-testid={`phrase-item-${p.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl truncate">{p.text}</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {p.tags.map((t) => (
                <span
                  key={t}
                  className={`tag ${["ritual","occult","death","kill","evil","beast"].includes(t) ? "tag-hot" : ""}`}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="hidden md:flex gap-5 font-mono text-xs">
            <KV label="Ord" v={p.ciphers.english_ordinal} />
            <KV label="Red" v={p.ciphers.full_reduction} />
            <KV label="Rev" v={p.ciphers.reverse_ordinal} />
            <KV label="Jew" v={p.ciphers.jewish} />
          </div>
          <button
            onClick={() => del(p.id, p.text)}
            className="btn-danger px-3 py-2 text-[11px] flex items-center gap-1"
            data-testid={`delete-${p.id}`}
          >
            <Trash2 size={12} />
          </button>
        </li>
      ))}
    </ul>
  );
}

function KV({ label, v }) {
  return (
    <div className="text-center">
      <div className="muted text-[9px] uppercase tracking-wider">{label}</div>
      <div className="gold">{v}</div>
    </div>
  );
}
